import {
  Button,
  Card,
  CheckmarkIcon,
  InformationIcon,
  Link,
  Row,
  Subtitle,
  Title,
} from 'internalLibrary';
import { FC, useMemo, useState } from 'react';
import { formatCurrencyOrMock } from 'formatters/formatCurrencyOrMock';
import { R } from 'config/app';
import { StopWatchIcon } from 'assets';
import { Tooltip } from 'react-tooltip';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import layout from '../../../../scss/layout.module.scss';
import LoanApplicationProcessLayout from 'ui/components/LoanApplicationProcessLayout';
import postFdfConfirmationApiCall from 'api/calls/postFdfConfirmationApiCall';
import style from './ApprovedManualScene.module.scss';
import useFdfQuery from 'ui/hooks/useFdfQuery';

const ApprovedManualScene: FC = () => {
  const [fdfConfirmed, setFdfConfirmed] = useState(false);
  const { t } = useTranslation();
  const fdfQuery = useFdfQuery();
  const fdfConfirmMutation = useMutation({
    mutationKey: ['client', 'application', 'fdf-confirmation'],
    mutationFn: postFdfConfirmationApiCall,
  });
  const navigate = useNavigate();

  const fastDeliveryFee = fdfQuery.fdf?.fastDeliveryFee;
  const isFDFButtonLoading = fdfConfirmed;

  const fdfConfirmClicked = () => {
    setFdfConfirmed(true);
    fdfConfirmMutation.mutate(undefined, {
      onSuccess() {
        navigate(R.FirstLoanFdfApplied);
      },
    });
  };

  const renderTopCardPart = useMemo(
    () => (
      <Row className={style['inner-card-top-row']}>
        <StopWatchIcon className={style['inner-card-icon']} />
        <Row className={style['inner-card-main-text-row']}>
          <span className={style['card-main-text']}>
            {t('approved_card_top')} <strong>10&nbsp;{t('minutes')}</strong>
            <InformationIcon className={style['info-icon']} id="infoIconCard" />
          </span>
        </Row>
      </Row>
    ),
    [t],
  );

  const renderBottomCardPart = useMemo(
    () => (
      <Row className={style['inner-card-bottom-row']}>
        <span> {t('approved_card_bottom')}</span>
        <span className={style.price}>
          <strong
            className={
              classNames()
              // panelStyles['value-loading'],
              // fastDeliveryFee !== undefined
              // ? panelStyles['value-loading--done']
              // : undefined,
            }
          >
            {formatCurrencyOrMock(
              fastDeliveryFee,
              '###',
              fastDeliveryFee === undefined,
            )}
          </strong>
        </span>
      </Row>
    ),
    [fastDeliveryFee, t],
  );

  return (
    <LoanApplicationProcessLayout>
      <div className={layout['width-lg']}>
        <Title
          icon={<CheckmarkIcon />}
          text={t('approved_title')}
          className={style.title}
        />

        <Row className={style.row}>
          <Subtitle
            htmlContent={t('approved_subtitle', { days: '7 – 10' })}
            className={style.subtitle}
          />
          <InformationIcon className={style['info-icon']} id="infoIconTop" />
        </Row>

        <Card className={style.card}>
          <div className={style.badge}>{t('or')}</div>
          <Card intent="success" className={style['inner-card']}>
            {renderTopCardPart}
            <hr className={style.divider} />
            {renderBottomCardPart}
          </Card>

          <div style={{ width: '100%' }} className={style['button-actor']}>
            <Button
              className={classNames(style.button)}
              onClick={fdfConfirmClicked}
              loading={isFDFButtonLoading}
            >
              {t('approved_card_button', { minutes: 10 })}
            </Button>
          </div>
        </Card>

        <div className={style['bottom-info']}>
          {t('approved_bottomInfo', { minutes: 10 })}
        </div>

        <div className={style['bottom-info']}>
          <span>{t('approved_bottomInfoTwo')}</span>{' '}
          <Link
            href={t('approved_bottomInfoTwoLinkUrl')}
            className={style['bottom-info-link']}
            target="_blank"
          >
            <span className={style['link-text']}>
              {t('approved_bottomInfoTwoLinkText')}
            </span>
          </Link>
          {'.'}
        </div>

        <Tooltip
          anchorId="infoIconTop"
          content={t('approved_subtitleTooltip')}
          variant="light"
          className={style.tooltip}
        />
        <Tooltip
          anchorId="infoIconCard"
          html={t('approved_card_tooltip')}
          variant="light"
          className={style.tooltip}
        />
      </div>
    </LoanApplicationProcessLayout>
  );
};

export default ApprovedManualScene;
